import type { HeavenlyStem } from '../types/index';
import type { EarthlyBranch } from '../types/index';
import type { TwelveStage, TwelveStageInfo } from '../types/index';
import type { FourPillars } from '../types/index';
import { stemIndex, branchIndex } from '../core/sexagenary';

const TWELVE_STAGES: readonly TwelveStage[] = [
  '장생', '목욕', '관대', '건록', '제왕', '쇠', '병', '사', '묘', '절', '태', '양',
] as const;

/**
 * 각 천간의 장생(長生) 시작 지지 인덱스.
 *
 * 양간(짝수 인덱스)은 순행, 음간(홀수 인덱스)은 역행한다.
 *
 * 甲(0): 장생 亥(11)  乙(1): 장생 午(6)
 * 丙(2): 장생 寅(2)   丁(3): 장생 酉(9)
 * 戊(4): 장생 寅(2)   己(5): 장생 酉(9)
 * 庚(6): 장생 巳(5)   辛(7): 장생 子(0)
 * 壬(8): 장생 申(8)   癸(9): 장생 卯(3)
 */
const CHANGSHENG_START: Record<number, number> = {
  0: 11, // 甲 -> 亥
  1: 6,  // 乙 -> 午
  2: 2,  // 丙 -> 寅
  3: 9,  // 丁 -> 酉
  4: 2,  // 戊 -> 寅
  5: 9,  // 己 -> 酉
  6: 5,  // 庚 -> 巳
  7: 0,  // 辛 -> 子
  8: 8,  // 壬 -> 申
  9: 3,  // 癸 -> 卯
};

/**
 * 일간의 특정 지지에서의 십이운성을 반환한다.
 *
 * 양간은 순행, 음간은 역행한다.
 */
export function getTwelveStage(dayMaster: HeavenlyStem, branch: EarthlyBranch): TwelveStageInfo {
  const sIdx = stemIndex(dayMaster);
  const bIdx = branchIndex(branch);
  const startBranch = CHANGSHENG_START[sIdx];
  const isYang = sIdx % 2 === 0;

  let stageIndex: number;
  if (isYang) {
    // Forward counting
    stageIndex = ((bIdx - startBranch) + 12) % 12;
  } else {
    // Backward counting
    stageIndex = ((startBranch - bIdx) + 12) % 12;
  }

  return {
    stage: TWELVE_STAGES[stageIndex],
    branch,
  };
}

/**
 * 일간 기준으로 사주 네 기둥 지지의 십이운성을 분석한다.
 */
export function analyzeTwelveStages(
  fourPillars: FourPillars,
  noHour?: boolean,
): Record<'year' | 'month' | 'day', TwelveStageInfo> & { hour?: TwelveStageInfo } {
  const dayMaster = fourPillars.day.stem;
  return {
    year: getTwelveStage(dayMaster, fourPillars.year.branch),
    month: getTwelveStage(dayMaster, fourPillars.month.branch),
    day: getTwelveStage(dayMaster, fourPillars.day.branch),
    ...(!noHour && { hour: getTwelveStage(dayMaster, fourPillars.hour.branch) }),
  };
}
